// Lib
import React, { isValidElement, ReactElement, useEffect, useRef, useState } from 'react';

// Utils
import classNames from 'classnames';

// Components
import Button from '../buttons/Button';
import Icon from '../icons/Icon';

// Types
import { SelectInputProps } from './SelectInput';

import './CustomSelectInput.scss';
import { createPortal } from 'react-dom';

const CustomSelectInput = ({ value, onChange, children, className, title, isCancellable }: SelectInputProps) => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
    const [modalHeight, setModalHeight] = useState<number>(0);
    const [positionAbove, setPositionAbove] = useState(false);

    const openModal = () => {
        setIsOpen(true);
    };
    const closeModal = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        if (buttonRef.current) {
            setButtonRect(buttonRef.current.getBoundingClientRect());

            if (isOpen && modalRef.current) {
                const buttonRect = buttonRef.current!.getBoundingClientRect();
                const modalRect = modalRef.current!.getBoundingClientRect();

                const height = modalRect.height;
                setModalHeight(height);

                const spaceBelow = window.innerHeight - buttonRect.bottom;
                const spaceAbove = buttonRect.top;

                setPositionAbove(spaceBelow < height && spaceAbove > height);
                setButtonRect(buttonRect);
            }
        }

        const handleOutsideClick = (event: MouseEvent) => {
            if (modalRef.current && modalRef.current.contains(event.target as Node)) return;

            event.stopPropagation();
            closeModal();
        };

        if (isOpen) {
            document.addEventListener('click', handleOutsideClick, true);
        } else {
            document.removeEventListener('click', handleOutsideClick, true);
        }

        return () => {
            document.removeEventListener('click', handleOutsideClick, true);
        };
    }, [isOpen]);

    const parsedOptions: { value: string; label: string }[] = React.Children.toArray(children)
        .filter(isValidElement)
        .map((child: ReactElement) => ({
            value: child.props.value,
            label: child.props.children,
        }));

    const handleAndroidSelect = (selectedValue: string) => {
        const tempSelect = document.createElement('select');
        const option = document.createElement('option');
        option.value = selectedValue;
        option.selected = true;
        tempSelect.appendChild(option);
        tempSelect.value = selectedValue;

        const event = {
            target: tempSelect,
            currentTarget: tempSelect,
        } as unknown as React.ChangeEvent<HTMLSelectElement>;

        onChange(event);
        closeModal();
    };

    const selectedLabel = parsedOptions.find((opt) => opt.value === value)?.label || 'Select';

    return (
        <>
            <div className="CustomSelectInput">
                <div
                    ref={buttonRef}
                    onClick={openModal}
                    className={classNames('select-button', className)}
                    style={{ cursor: 'pointer' }}
                >
                    {selectedLabel}
                </div>

                {isOpen &&
                    buttonRect &&
                    createPortal(
                        <div
                            ref={modalRef}
                            className="custom-select-input-modal"
                            style={
                                {
                                    '--select-modal-top': `${
                                        positionAbove
                                            ? buttonRect.top + window.scrollY - modalHeight
                                            : buttonRect.bottom + window.scrollY
                                    }px`,
                                    '--select-modal-left': `${buttonRect.left + window.scrollX}px`,
                                } as React.CSSProperties
                            }
                        >
                            <div className="select-title">{title}</div>
                            {parsedOptions.map((option) => {
                                const isSelected = option.value === value;
                                return (
                                    <li key={option.value}>
                                        <Button
                                            className={classNames('select-option', { selected: isSelected })}
                                            onClick={() => {
                                                handleAndroidSelect(option.value);
                                            }}
                                        >
                                            <span className="select-option-label">{option.label}</span>
                                            {isSelected ? <Icon name="tick" /> : null}
                                        </Button>
                                    </li>
                                );
                            })}
                            {isCancellable && <Button onClick={closeModal}>Cancel</Button>}
                        </div>,
                        document.body,
                    )}
            </div>
        </>
    );
};

export default CustomSelectInput;
